// src/components/admin/DeactivationReasonModal.tsx

import React, { useState, useEffect } from 'react';
import { X, AlertTriangle, Info, Check } from 'lucide-react';

interface DeactivationReasonModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: (reason: string) => void;
    entityType: 'product' | 'variant';
    entityName: string;
    isProcessing?: boolean;
    isLastActive?: boolean;
}

const REASON_OPTIONS = [
    { value: 'policy_violation', label: 'Policy Violation' },
    { value: 'inappropriate_content', label: 'Inappropriate Content' },
    { value: 'copyright_infringement', label: 'Copyright Infringement' },
    { value: 'misleading_information', label: 'Misleading Information' },
    { value: 'quality_issues', label: 'Quality Issues' },
    { value: 'customer_complaints', label: 'Customer Complaints' },
    { value: 'vendor_request', label: 'Vendor Request' },
    { value: 'other', label: 'Other' },
];

const DeactivationReasonModal: React.FC<DeactivationReasonModalProps> = ({
    isOpen,
    onClose,
    onConfirm,
    entityType,
    entityName,
    isProcessing = false,
    isLastActive = false,
}) => {
    const [selectedReason, setSelectedReason] = useState<string>('');
    const [additionalDetails, setAdditionalDetails] = useState('');
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (isOpen) {
            setSelectedReason('');
            setAdditionalDetails('');
            setError(null);
        }
    }, [isOpen]);

    if (!isOpen) return null;

    const getFullReason = (): string => {
        const selectedLabel = REASON_OPTIONS.find(r => r.value === selectedReason)?.label || '';
        if (selectedReason === 'other') {
            return additionalDetails.trim() || 'Other (no details provided)';
        }
        if (additionalDetails.trim()) {
            return `${selectedLabel}: ${additionalDetails.trim()}`;
        }
        return selectedLabel;
    };

    const handleConfirm = () => {
        if (!selectedReason) {
            setError('Please select a reason for deactivation');
            return;
        }

        if (selectedReason === 'other' && !additionalDetails.trim()) {
            setError('Please provide details for the "Other" reason');
            return;
        }

        setError(null);
        onConfirm(getFullReason());
    };

    const handleClose = () => {
        setSelectedReason('');
        setAdditionalDetails('');
        setError(null);
        onClose();
    };

    const title = entityType === 'product' ? 'Deactivate Product' : 'Deactivate Variant';
    const description = entityType === 'product'
        ? `You are about to deactivate "${entityName}". This will remove it from the store and set the status to "Changes Requested".`
        : `You are about to deactivate the variant "${entityName}". This will remove this variant from the store.`;

    const fullReasonPreview = selectedReason ? getFullReason() : '';

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
            <div className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto shadow-2xl">
                {/* Header */}
                <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <AlertTriangle className="h-5 w-5 text-amber-500" />
                        <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
                    </div>
                    <button
                        type="button"
                        onClick={handleClose}
                        className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
                        disabled={isProcessing}
                    >
                        <X size={20} className="text-gray-500" />
                    </button>
                </div>

                {/* Body */}
                <div className="p-6 space-y-4">
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                        <div className="flex items-start gap-2">
                            <Info className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                            <p className="text-sm text-amber-800">{description}</p>
                        </div>
                    </div>

                    {isLastActive && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                            <p className="text-sm text-red-700">
                                <strong>Warning:</strong> This is the last active variant. Confirming will also deactivate the product.
                            </p>
                        </div>
                    )}

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Deactivation Reason <span className="text-red-500">*</span>
                        </label>
                        <div className="space-y-2">
                            {REASON_OPTIONS.map((option) => (
                                <button
                                    key={option.value}
                                    type="button"
                                    onClick={() => {
                                        setSelectedReason(option.value);
                                        setError(null);
                                    }}
                                    className={`w-full text-left px-4 py-3 rounded-lg border-2 transition-colors flex items-center justify-between ${selectedReason === option.value
                                            ? 'border-blue-500 bg-blue-50'
                                            : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                                        }`}
                                >
                                    <span className={`text-sm ${selectedReason === option.value ? 'text-blue-700 font-medium' : 'text-gray-700'}`}>
                                        {option.label}
                                    </span>
                                    {selectedReason === option.value && (
                                        <Check className="h-4 w-4 text-blue-600" />
                                    )}
                                </button>
                            ))}
                        </div>
                        {error && (
                            <p className="mt-2 text-sm text-red-600">{error}</p>
                        )}
                    </div>

                    <div>
                        <label htmlFor="additional_details" className="block text-sm font-medium text-gray-700 mb-1">
                            Additional Details
                            {selectedReason === 'other' && <span className="text-red-500 ml-1">*</span>}
                        </label>
                        <textarea
                            id="additional_details"
                            value={additionalDetails}
                            onChange={(e) => {
                                setAdditionalDetails(e.target.value);
                                if (error && selectedReason !== 'other') setError(null);
                            }}
                            placeholder={selectedReason === 'other'
                                ? "Please provide details for why this is being deactivated..."
                                : "Optional: Add any additional context or notes..."
                            }
                            rows={3}
                            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none ${error && selectedReason === 'other' && !additionalDetails.trim()
                                    ? 'border-red-500 bg-red-50'
                                    : 'border-gray-300'
                                }`}
                            disabled={isProcessing}
                        />
                    </div>

                    {/* Reason Preview */}
                    {selectedReason && (
                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                            <p className="text-xs font-medium text-gray-500 mb-1">Reason Preview:</p>
                            <p className="text-sm text-gray-700">
                                {fullReasonPreview || 'Select a reason above to preview'}
                            </p>
                        </div>
                    )}

                    <div className="bg-gray-50 rounded-lg p-3">
                        <p className="text-xs text-gray-500">
                            <strong>What happens next:</strong>
                            <br />
                            • The {entityType} will be deactivated immediately
                            <br />
                            • The vendor will receive an email with the reason
                            <br />
                            • The vendor must make changes and resubmit for review
                        </p>
                    </div>
                </div>

                {/* Footer */}
                <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
                    <button
                        type="button"
                        onClick={handleClose}
                        className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium text-gray-700"
                        disabled={isProcessing}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={handleConfirm}
                        className={`px-4 py-2 rounded-lg text-sm font-medium text-white transition-colors ${isProcessing
                                ? 'bg-gray-400 cursor-not-allowed'
                                : 'bg-red-600 hover:bg-red-700'
                            }`}
                        disabled={isProcessing}
                    >
                        {isProcessing ? 'Deactivating...' : `Deactivate ${entityType === 'product' ? 'Product' : 'Variant'}`}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default DeactivationReasonModal;